import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconPrinter } from './fieldIcons';

/**
 * How many labels, asked once before printing (Rhys 2026-08-29).
 *
 * Printing used to silently use "actual quantities" (the per-product label
 * count, falling back to stock on hand), which is right when labelling a
 * delivery and wrong every other time — staff were printing 40 labels when
 * they wanted one.
 *
 * Everything is one click: the two fixed answers PRINT ON CLICK, and the
 * Custom row keeps its number input visible at all times — typing a number
 * makes its Print button appear beside it.
 *
 * Two shells, one body (same pattern as BoxDimensionsModal):
 *  - inline: lays itself over the CARD's own detail area, so staff answer
 *    where they were already looking (Rhys 2026-08-29). Used by the card's
 *    Print label button.
 *  - portal: centred dialog over a backdrop, for the toolbar's multi-select
 *    print where there is no single card to anchor to.
 */
export default function PrintLabelQuantityModal({
  open,
  productCount = 0,
  // Sum of the label counts entered on the selected cards. null = nothing is
  // selected (a lone card's Print label with the box unticked), so the row
  // is not offered at all: only "1 label each" and Custom (Rhys 2026-09-03).
  enteredTotal = null,
  onCancel,
  onConfirm,
  darkMode = false,
  inline = false
}) {
  const [custom, setCustom] = useState('');
  const customRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setCustom('');
    // Bubble phase, no stopPropagation: the capture-phase version swallowed
    // Escape app-wide while any card's dialog was open, so another card's
    // field editor could never close itself (review find, 2026-08-29).
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const customQty = Math.max(0, parseInt(custom, 10) || 0);
  const customTotal = useMemo(() => productCount * customQty, [productCount, customQty]);

  if (!open) return null;

  const surface = darkMode ? '#1f2937' : '#ffffff';
  const ink = darkMode ? '#e5e7eb' : '#202223';
  const inkMuted = darkMode ? '#9ca3af' : '#6d7175';
  const border = darkMode ? '#374151' : '#e1e3e5';
  const accent = '#0d9488';
  const pad = inline ? '8px 12px' : '12px 14px';

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    textAlign: 'left', padding: pad, borderRadius: 10, cursor: 'pointer',
    border: `1px solid ${border}`, background: 'transparent', color: 'inherit', font: 'inherit',
    minWidth: 0
  };
  const hoverOn = (e) => {
    e.currentTarget.style.borderColor = accent;
    e.currentTarget.style.background = darkMode ? 'rgba(13,148,136,0.15)' : '#f0fdfa';
  };
  const hoverOff = (e) => {
    e.currentTarget.style.borderColor = border;
    e.currentTarget.style.background = 'transparent';
  };

  const Row = ({ title, blurb, count, onClick }) => (
    <button type="button" onClick={onClick} style={rowStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: inkMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{blurb}</span>
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0,
        fontSize: 12.5, fontWeight: 700, color: accent
      }}>
        <IconPrinter />
        {count}
      </span>
    </button>
  );

  const panel = (
      <div
        role="dialog"
        aria-label="How many labels?"
        onClick={(e) => e.stopPropagation()}
        style={inline ? {
          position: 'absolute', inset: 0, zIndex: 30,
          background: surface, color: ink,
          border: `1px solid ${border}`, borderRadius: 10,
          boxShadow: '0 6px 22px rgba(0,0,0,0.22)',
          display: 'flex', flexDirection: 'column',
          overflow: 'auto'
        } : {
          width: 'min(460px, 100%)', background: surface, color: ink,
          borderRadius: 12, border: `1px solid ${border}`,
          boxShadow: '0 20px 48px rgba(15, 23, 42, 0.28)', overflow: 'hidden'
        }}
      >
        <div style={{ padding: inline ? '8px 12px 4px' : '14px 16px', borderBottom: inline ? 'none' : `1px solid ${border}`, flexShrink: 0 }}>
          <div style={{ fontSize: inline ? 13 : 15, fontWeight: 700 }}>How many labels?</div>
          {!inline && (
            <div style={{ fontSize: 12, color: inkMuted, marginTop: 2 }}>
              {productCount} product{productCount === 1 ? '' : 's'} selected · pick one to print
            </div>
          )}
        </div>

        <div style={{ padding: inline ? '4px 12px 8px' : 12, display: 'flex', flexDirection: 'column', gap: inline ? 6 : 8 }}>
          {/* "Actual quantities" read as "what we have in stock" — it is the
              count typed into the Labels box on each card, nothing else. */}
          {enteredTotal !== null && enteredTotal !== undefined && (
            <Row
              title={productCount === 1 ? 'Labels as entered' : 'Labels as entered on each card'}
              blurb={productCount === 1
                ? 'The number in this card\'s Labels box.'
                : 'The number in each selected card\'s Labels box.'}
              count={`${enteredTotal} label${enteredTotal === 1 ? '' : 's'}`}
              onClick={() => onConfirm?.({ mode: 'actual', perProduct: null })}
            />
          )}
          <Row
            title="1 label each"
            blurb="A single label per product."
            count={`${productCount} label${productCount === 1 ? '' : 's'}`}
            onClick={() => onConfirm?.({ mode: 'one', perProduct: 1 })}
          />

          {/* The number box is always on show — no click to reveal it. */}
          <div
            onClick={() => customRef.current?.focus()}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: pad, borderRadius: 10, cursor: 'text', minWidth: 0,
              border: `1px solid ${customQty > 0 ? accent : border}`,
              background: customQty > 0
                ? (darkMode ? 'rgba(13,148,136,0.15)' : '#f0fdfa')
                : 'transparent'
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 600, flexShrink: 0 }}>Custom</span>
            <input
              ref={customRef}
              type="number"
              min="1"
              value={custom}
              placeholder="0"
              onChange={(e) => setCustom(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && customQty > 0) onConfirm?.({ mode: 'custom', perProduct: customQty });
                if (e.key === 'Escape') onCancel?.();
              }}
              style={{
                width: 70, padding: '5px 8px', fontSize: 13, borderRadius: 6,
                border: `1px solid ${border}`, background: darkMode ? '#111827' : '#fff',
                color: ink, outline: 'none'
              }}
            />
            <span style={{ fontSize: 11.5, color: inkMuted, flexShrink: 0 }}>per product</span>
            {/* Print appears once the number is real, so the row cannot be
                actioned into a zero-label job. */}
            {customQty > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onConfirm?.({ mode: 'custom', perProduct: customQty }); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  marginLeft: 'auto', flexShrink: 0,
                  padding: '6px 12px', fontSize: 12.5, fontWeight: 700,
                  borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: accent, color: '#fff'
                }}
              >
                <IconPrinter />
                Print {customTotal}
              </button>
            )}
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
          marginTop: inline ? 'auto' : 0,
          padding: inline ? '4px 12px 8px' : '10px 16px',
          borderTop: inline ? 'none' : `1px solid ${border}`,
          background: inline ? 'transparent' : (darkMode ? 'rgba(255,255,255,0.02)' : '#fafbfb')
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '6px 14px', fontSize: 13, borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${border}`, background: 'transparent', color: 'inherit'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
  );

  if (inline) return panel;

  return createPortal(
    <div
      role="presentation"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 10060,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(15, 23, 42, 0.45)'
      }}
    >
      {panel}
    </div>,
    document.body
  );
}
